import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Bundle tier selector with per-piece variant choice.
 *
 * A "buy 2, get 1 free" tier puts three separate garments in the cart, and a
 * shopper almost never wants three identical ones — so each piece gets its own
 * colour/size selects rather than one picker driving a quantity.
 */
/*
 * The theme declares `const Theme = {...}` in a classic script. A top-level const
 * is a LEXICAL global, not a property of window - so bare `Theme` resolves while
 * `window.Theme` is undefined. Read it by identifier, with route fallbacks.
 */
function themeGlobal() {
  return typeof Theme !== 'undefined' ? Theme : undefined;
}

function cartRoutes() {
  const t = themeGlobal();
  return {
    add: t?.routes?.cart_add_url ?? '/cart/add.js',
    cart: t?.routes?.cart_url ?? '/cart',
  };
}

export class BundleTiersComponent extends Component {
  requiredRefs = ['pieces', 'submit'];

  connectedCallback() {
    super.connectedCallback();

    try {
      this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
    } catch {
      this.data = { options: [], variants: [] };
    }

    this.addEventListener('change', this.#onChange);
    this.#renderPieces();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLElement)) return;

    if (t.matches('.bt__radio')) {
      this.#renderPieces();
    } else if (t.matches('[data-option]')) {
      this.#sync();
    }
  };

  get #tier() {
    const r = this.querySelector('.bt__radio:checked');
    return {
      pay: Number(r?.dataset.pay || 1),
      free: Number(r?.dataset.free || 0),
      units: Number(r?.dataset.units || 1),
    };
  }

  /** Rebuild one card of selects per piece whenever the tier changes. */
  #renderPieces() {
    const { units } = this.#tier;
    const { pieces } = this.refs;
    const opts = this.data.options || [];

    pieces.innerHTML = '';

    for (let i = 0; i < units; i++) {
      const card = document.createElement('div');
      card.className = 'bt__piece';
      card.dataset.piece = String(i);

      const label = document.createElement('span');
      label.className = 'bt__piece-label';
      label.textContent = this.dataset.pieceLabel?.replace('[n]', String(i + 1)) || `Piece ${i + 1}`;
      card.appendChild(label);

      const row = document.createElement('div');
      row.className = 'bt__piece-opts';

      opts.forEach((opt, oi) => {
        const wrap = document.createElement('label');
        wrap.className = 'bt__field';

        const name = document.createElement('span');
        name.className = 'bt__field-name';
        name.textContent = opt.name;

        const sel = document.createElement('select');
        sel.className = 'bt__select';
        sel.dataset.option = String(oi);
        opt.values.forEach((v) => {
          const o = document.createElement('option');
          o.value = v;
          o.textContent = v;
          sel.appendChild(o);
        });

        wrap.append(name, sel);
        row.appendChild(wrap);
      });

      card.appendChild(row);

      const note = document.createElement('span');
      note.className = 'bt__piece-note';
      card.appendChild(note);

      pieces.appendChild(card);
    }

    this.#sync();
  }

  /** Variant whose option values match this piece's selects, if any. */
  #variantFor(card) {
    const chosen = Array.from(card.querySelectorAll('[data-option]')).map((s) => s.value);
    return (this.data.variants || []).find(
      (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
    );
  }

  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'AUD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /** Recompute totals and per-tier pricing from the pieces actually selected. */
  #sync() {
    const { free } = this.#tier;
    const cards = Array.from(this.querySelectorAll('.bt__piece'));

    this.selection = [];
    let blocked = false;

    for (const card of cards) {
      const v = this.#variantFor(card);
      const note = card.querySelector('.bt__piece-note');

      if (!v) {
        note.textContent = 'That combination does not exist.';
        blocked = true;
      } else if (!v.available) {
        note.textContent = 'Sold out — pick another size or colour.';
        blocked = true;
      } else {
        note.textContent = '';
        this.selection.push(v);
      }
      card.dataset.invalid = String(!v || !v.available);
    }

    // BxGy discounts the CHEAPEST lines, so mirror that when showing the total.
    const prices = this.selection.map((v) => v.price).sort((a, b) => a - b);
    const freed = prices.slice(0, Math.min(free, prices.length)).reduce((s, p) => s + p, 0);
    const full = prices.reduce((s, p) => s + p, 0);
    const payable = full - freed;

    const { total, saving, submit } = this.refs;
    if (total) total.textContent = this.#money(payable);
    if (saving) {
      saving.hidden = freed <= 0;
      if (freed > 0) saving.textContent = 'Save ' + this.#money(freed);
    }

    // Per-tier headline pricing uses the first piece as the reference unit.
    const unit = this.selection[0]?.price ?? this.data.variants?.[0]?.price ?? 0;
    for (const tier of this.querySelectorAll('[data-tier]')) {
      const r = tier.querySelector('.bt__radio');
      const pay = Number(r.dataset.pay || 1);
      const units = Number(r.dataset.units || 1);
      tier.querySelector('[data-total]').textContent = this.#money(unit * pay);
      tier.querySelector('[data-perunit]').textContent = this.#money((unit * pay) / units) + ' each';
      const s = tier.querySelector('[data-save]');
      const diff = unit * units - unit * pay;
      s.hidden = diff <= 0;
      if (diff > 0) s.textContent = 'Save ' + this.#money(diff);
      tier.dataset.selected = String(r.checked);
    }

    if (submit) submit.disabled = blocked || this.selection.length === 0;
  }

  handleSubmit = (event) => {
    event.preventDefault();
    if (!this.selection?.length) return;

    const { submit } = this.refs;
    submit.setAttribute('aria-busy', 'true');

    // Merge duplicate picks into one line with a quantity.
    const counts = new Map();
    for (const v of this.selection) counts.set(v.id, (counts.get(v.id) || 0) + 1);
    const items = [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    fetch(cartRoutes().add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((r) => r.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          return;
        }

        const cart = await fetch(`${cartRoutes().cart}.json`, {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: false,
            items: cart.items,
            source: 'bundle-tiers-component',
            sourceId: 'bundle-tiers',
            itemCount: items.reduce((s, i) => s + i.quantity, 0),
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        console.warn('[bundle-tiers] add failed:', error);
      })
      .finally(() => submit.removeAttribute('aria-busy'));
  };
}

if (!customElements.get('bundle-tiers')) {
  customElements.define('bundle-tiers', BundleTiersComponent);
}
